<template>
  <form-layout v-if="isSpAdmin" title="Manage clients" hide-bottom-bar @on-dismiss="dismiss">
    <create-client-form
      v-if="showCreateClientForm"
      :initial-client-name="currentSearchQuery"
      @create-client-success="handleClientCreatedSuccess"
      @create-client-failure="handleClientCreatedFailure"
      @discard="
        () => {
          showCreateClientForm = false
        }
      "
    />
    <template v-if="!showCreateClientForm" #extras>
      <client-search @update-search="handleUpdateSearch" />
    </template>

    <SubscriptionOverlay
      :is-overlay-visible="!canCreateClient"
      learn-more-url="https://www.ironsight.app/upgrade-to-pro"
    >
      <clients-list
        v-if="!showCreateClientForm"
        ref="clientList"
        :creating-connection="creatingConnection"
        :search="currentSearchQuery"
        @select="onSelect"
        @open-create-client-form="handleOpenCreateClientForm"
      />
    </SubscriptionOverlay>

    <template #snack-bar-wrapper>
      <vs-snackbar v-model="isSnackbarVisible" :type="snackbarType" sticky>
        {{ snackbarMessage ?? '' }}
      </vs-snackbar>
    </template>
  </form-layout>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
import { useStore } from 'vuex-composition-helpers'
import { useCreateConnectionRequestMutation } from '@/api/connection-request'
import FormLayout from '@/components/common/FormLayout.vue'
import VsSnackbar from '@/components/vision/VsSnackbar.vue'
import CreateClientForm from '@/components/companies/CreateClientForm.vue'
import ClientsList from '@/components/companies/ClientsList.vue'
import ClientSearch from '@/components/companies/ClientSearch.vue'
import { AxiosError } from 'axios'
import SubscriptionOverlay from '@/components/vision/next/SubscriptionOverlay.vue'
import { SnackType } from '@/components/vision/SnackBarType'

const props = defineProps<{
  onBackRoute: string
  onSaveAndCreateRoute: string
}>()

const clientList = ref()

const route = useRoute()
const router = useRouter()

const isSnackbarVisible = ref(false)
const snackbarMessage = ref()
const showCreateClientForm = ref(false)
const currentSearchQuery = ref('')
const snackbarType = ref(SnackType.ERROR)

const store = useStore()
const currentUserOrganizationId = computed<string>(
  () => store.getters['permissions/getOrganizationId'],
)
const canCreateClient = computed<boolean>(() => store.getters['permissions/canCreateClient'])
const isSpAdmin = computed<boolean>(() => store.getters['permissions/isSpAdmin'])

const { mutateAsync: createConnection, isLoading: creatingConnection } =
  useCreateConnectionRequestMutation()

const handleClientCreatedSuccess = async (data: {
  clientHubId: string
  clientHubName: string
  createNewJob: boolean
}) => {
  if (data.createNewJob) {
    await router.push({
      name: props.onSaveAndCreateRoute,
      params: {
        id: 'new',
        hubId: data.clientHubId,
      },
    })
  } else {
    showCreateClientForm.value = false
    currentSearchQuery.value = ''
    snackbarType.value = SnackType.INFO
    isSnackbarVisible.value = true
    snackbarMessage.value = `${data.clientHubName} has been created`
  }
}

const handleClientCreatedFailure = (error: unknown) => {
  snackbarType.value = SnackType.ERROR
  isSnackbarVisible.value = true
  snackbarMessage.value = error
}

const dismiss = () => {
  router.push({ name: props.onBackRoute, params: route.params })
}

const onSelect = async (hubId: string) => {
  await save(hubId)
}

async function save(hubId: string) {
  try {
    await createConnection({
      recipientBusinessUnitId: hubId,
      requesterBusinessUnitId: currentUserOrganizationId.value,
    })
    // Fetching the current user context updates which hubs the user has access to
    // when creating jobs. Will get any hubs that have been auto partnered
    store.dispatch('permissions/fetchCurrentUserContext')
    store.dispatch('fetchJobData', true)
  } catch (err) {
    snackbarType.value = SnackType.ERROR
    isSnackbarVisible.value = true

    if (err instanceof AxiosError) {
      snackbarMessage.value = err.response?.data.detail
    }
  }
}

const handleUpdateSearch = (newSearchQuery: string) => {
  currentSearchQuery.value = newSearchQuery
  clientList.value.updatePage(1)
}

const handleOpenCreateClientForm = () => {
  showCreateClientForm.value = true
}
</script>
